// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Slider as ReactSlider, Rail as Rails, Handles, Tracks } from 'react-compound-slider';
import { cx } from '@linaria/core';

import styles from './styles';
import { Rail, Track, Handle } from './common';
import { SingleSliderProps } from './types';

const truncateNumber = (value: number, step: number) => {
  return (Number.isInteger(step) ? value : value.toFixed(2)).toString();
};

const defaultFormat = (value: number, step?: number): string => {
  return truncateNumber(value, step);
};

const SingleSlider = (props: SingleSliderProps) => {
  const {
    id,
    min = 0,
    max = 100,
    step = 1,
    value,
    width = '100%',
    label,
    showValue = true,
    onUpdate,
    onChange,
    formatValue = defaultFormat,
    disabled,
    rtl = false,
    handleNames,
    handleIds,
    className,
    accentColor,
    ariaLabel,
    ariaLabelledBy,
    ariaValueText,
    ...restProps
  } = props;

  const domain = useMemo(() => [min, max], [min, max]);

  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleUpdate = useCallback(
    ([updatedValue]: ReadonlyArray<number>) => {
      setLocalValue(updatedValue);
      if (onUpdate) {
        onUpdate(updatedValue);
      }
    },
    [onUpdate]
  );

  const handleChange = useCallback(
    ([updatedValue]: ReadonlyArray<number>) => {
      onChange(updatedValue);
    },
    [onChange]
  );

  return (
    <div
      data-disabled={disabled ? '' : undefined}
      className={cx(styles.container, className)}
      style={{ width }}
      {...restProps}
    >
      <div className={styles.labels}>
        <label htmlFor={id}>{label}</label>
        {showValue ? (
          <div aria-valuetext={formatValue(localValue, step)}>{formatValue(localValue, step)}</div>
        ) : null}
      </div>

      <div className={styles.wrapper}>
        <ReactSlider
          step={step}
          disabled={disabled}
          domain={domain}
          values={[value]}
          reversed={rtl}
          onUpdate={handleUpdate}
          onChange={onChange && handleChange}
        >
          <Rails>
            {({ getRailProps }) => <Rail getRailProps={getRailProps} disabled={disabled} />}
          </Rails>
          <Tracks left={!rtl} right={rtl}>
            {({ tracks, getTrackProps }) => (
              <div>
                {tracks.map(({ id, source, target }) => (
                  <Track
                    key={id}
                    disabled={disabled}
                    source={source}
                    target={target}
                    getTrackProps={getTrackProps}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            )}
          </Tracks>

          <Handles>
            {({ handles, getHandleProps }) => (
              <div className={styles.handleWrapper}>
                {handles.map((handle, index) => (
                  <Handle
                    key={handle.id}
                    disabled={disabled}
                    handle={handle}
                    domain={domain}
                    getHandleProps={getHandleProps}
                    accessibilityText={handleNames ? handleNames[index] : undefined}
                    data-testid={handleIds ? handleIds[index] : undefined}
                    accentColor={accentColor}
                    ariaLabel={ariaLabel}
                    ariaLabelledBy={ariaLabelledBy}
                    ariaValueText={ariaValueText}
                  />
                ))}
              </div>
            )}
          </Handles>
        </ReactSlider>
      </div>
    </div>
  );
};

export default memo(SingleSlider);
